/* PULLHOUSE — carousels, tabs, countdowns, quick view, mobile drawer tabs */
(() => {
  if (window.PullHouse) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const money = (cents) => {
    const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    return new Intl.NumberFormat(cur === 'USD' ? 'en-US' : 'en-GB', { style: 'currency', currency: cur }).format(cents / 100);
  };
  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- Carousels ---------- */
  function initCarousel(el) {
    if (el.dataset.phReady) return;
    el.dataset.phReady = '1';
    const track = el.querySelector('.ph-carousel__track');
    const prev = el.querySelector('.ph-carousel__btn--prev');
    const next = el.querySelector('.ph-carousel__btn--next');
    if (!track) return;
    const step = () => {
      const item = track.firstElementChild;
      return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 20) : track.clientWidth;
    };
    const update = () => {
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------- Tabs ---------- */
  function initTabs(el) {
    if (el.dataset.phReady) return;
    el.dataset.phReady = '1';
    const tabs = [...el.querySelectorAll('[role="tab"]')];
    const select = (tab) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = el.querySelector('#' + t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      const panel = el.querySelector('#' + tab.getAttribute('aria-controls'));
      panel && panel.querySelectorAll('.ph-carousel').forEach(initCarousel);
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(t));
      t.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        n.focus(); select(n);
      });
    });
  }

  /* ---------- Countdown ---------- */
  function initCountdown(el) {
    if (el.dataset.phReady) return;
    el.dataset.phReady = '1';
    const end = Date.parse(el.dataset.end);
    if (!end) return;
    const parts = { d: el.querySelector('[data-d]'), h: el.querySelector('[data-h]'), m: el.querySelector('[data-m]'), s: el.querySelector('[data-s]') };
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      let left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      const d = Math.floor(left / 86400); left -= d * 86400;
      const h = Math.floor(left / 3600); left -= h * 3600;
      const m = Math.floor(left / 60); const s = left - m * 60;
      if (parts.d) parts.d.textContent = d;
      if (parts.h) parts.h.textContent = pad(h);
      if (parts.m) parts.m.textContent = pad(m);
      if (parts.s) parts.s.textContent = pad(s);
      if (end <= Date.now()) { el.classList.add('is-ended'); clearInterval(timer); }
    };
    const timer = setInterval(tick, 1000);
    tick();
  }

  /* ---------- Quick view ---------- */
  let qv;
  function buildQV() {
    qv = document.createElement('div');
    qv.className = 'ph-qv';
    qv.setAttribute('role', 'dialog');
    qv.setAttribute('aria-modal', 'true');
    qv.setAttribute('aria-label', 'Quick view');
    qv.innerHTML = '<div class="ph-qv__box"><button type="button" class="ph-qv__close" aria-label="Close">×</button><div class="ph-qv__content"></div></div>';
    document.body.appendChild(qv);
    qv.addEventListener('click', (e) => { if (e.target === qv || e.target.closest('.ph-qv__close')) closeQV(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && qv.classList.contains('is-open')) closeQV(); });
  }
  let lastFocus;
  function closeQV() { qv.classList.remove('is-open'); document.documentElement.style.overflow = ''; lastFocus && lastFocus.focus(); }
  async function openQV(handle, trigger) {
    if (!qv) buildQV();
    lastFocus = trigger;
    const box = qv.querySelector('.ph-qv__box');
    box.innerHTML = '<button type="button" class="ph-qv__close" aria-label="Close">×</button><p style="grid-column:1/-1;text-align:center;padding:4rem">Loading…</p>';
    qv.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    try {
      const res = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js');
      if (!res.ok) throw new Error();
      const p = await res.json();
      const pre = /pre-?order/i.test(p.title) || (p.tags || []).some((t) => /pre-?order/i.test(t)) || (trigger && trigger.closest('.ph-card') && trigger.closest('.ph-card').querySelector('.ph-badge--preorder'));
      const title = p.title.replace(/\s*\(Pre-Order\)/i, '');
      // Volume-price variants ("Buy 2+ …") are applied automatically in the bag, so only real choices are listed here.
      const isTier = (v) => /(^| \/ )Buy \d+\+ · /.test(v.title || '');
      const variants = (p.variants || []).filter((v) => !isTier(v));
      const tierOnly = (v) => (v.title || '').replace(/( \/ )?Standard price$/, '') || 'Default';
      const first = variants.find((v) => v.available) || variants[0];
      const opts = variants.length > 1 ? '<label class="ph-sr" for="PhQvVariant">Option</label><select id="PhQvVariant" name="id">' + variants.map((v) => '<option value="' + v.id + '"' + (v === first ? ' selected' : '') + (v.available ? '' : ' disabled') + '>' + esc(tierOnly(v)) + ' – ' + money(v.price) + (v.available ? '' : ' (sold out)') + '</option>').join('') + '</select>' : '<input type="hidden" name="id" value="' + (first ? first.id : '') + '">';
      const img = p.featured_image ? '<img src="' + esc(p.featured_image) + '&width=900" alt="' + esc(title) + '">' : '';
      const pmin = Math.min(...variants.map((v) => v.price)), pmax = Math.max(...variants.map((v) => v.price));
      const price = pmax > pmin ? money(pmin) + ' – ' + money(pmax) : money(pmin);
      const btn = !p.available ? '<button class="ph-btn ph-btn--disabled" disabled>Sold out</button>' : '<button type="submit" class="ph-btn' + (pre ? ' ph-btn--preorder' : '') + '">' + (pre ? 'Pre-order now' : 'Add to cart') + '</button>';
      box.innerHTML = '<button type="button" class="ph-qv__close" aria-label="Close">×</button>' +
        '<div class="ph-qv__media">' + img + '</div>' +
        '<div class="ph-qv__info"><h2>' + esc(title) + '</h2><div class="ph-qv__price">' + price + '</div>' +
        '<div class="ph-qv__desc">' + (p.description || '') + '</div>' +
        '<form method="post" action="' + root + 'cart/add" class="ph-qv__actions">' + opts + '<input type="hidden" name="quantity" value="1">' + btn +
        '<a class="ph-qv__more" href="' + esc(p.url) + '">View full details →</a></form></div>';
      box.querySelector('.ph-qv__close').focus();
    } catch (e) {
      box.innerHTML = '<button type="button" class="ph-qv__close" aria-label="Close">×</button><p style="grid-column:1/-1;text-align:center;padding:4rem">Sorry, this product could not be loaded. <a href="' + root + 'products/' + encodeURIComponent(handle) + '">Open product page</a></p>';
    }
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-ph-quick-view]');
    if (!b) return;
    e.preventDefault();
    openQV(b.dataset.phQuickView, b);
  });

  /* ---------- Mobile drawer: Menu / Categories tabs ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-ph-drawer-tab]');
    if (!b) return;
    const wrap = b.closest('[data-ph-drawer]');
    wrap.querySelectorAll('[data-ph-drawer-tab]').forEach((t) => t.setAttribute('aria-selected', String(t === b)));
    wrap.querySelectorAll('[data-ph-drawer-panel]').forEach((p) => (p.hidden = p.dataset.phDrawerPanel !== b.dataset.phDrawerTab));
  });

  /* ---------- Init ---------- */
  function init(scope = document) {
    scope.querySelectorAll('.ph-carousel').forEach((el) => { if (!el.closest('[hidden]')) initCarousel(el); });
    scope.querySelectorAll('[data-ph-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-ph-countdown]').forEach(initCountdown);
  }
  document.addEventListener('DOMContentLoaded', () => init());
  if (document.readyState !== 'loading') init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  window.PullHouse = { init, openQuickView: openQV };
})();
